import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Coffee, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function AdminLogin() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) navigate("/admin", { replace: true });
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back.");
            navigate("/admin", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-cream-light" data-testid="admin-login">
            {/* Left brand panel */}
            <div className="hidden md:flex md:w-1/2 text-cream p-12 flex-col justify-between relative overflow-hidden" style={{ backgroundColor: "#1a0f00" }}>
                <div className="grain-light" />
                <div className="relative z-10">
                    <div className="tracked text-gold text-[10px]">CAFE HIDEOUT · CMS</div>
                </div>
                <div className="relative z-10">
                    <Coffee size={48} strokeWidth={1.1} className="text-gold mb-6" />
                    <h1 className="font-editorial font-bold text-cream text-5xl lg:text-6xl leading-[0.95]">
                        Pour the page,<br /> not the code.
                    </h1>
                    <p className="font-body text-cream/70 mt-6 max-w-md text-base">
                        A quiet panel to edit every word, swap every photo, and tend the brand from anywhere.
                    </p>
                </div>
                <div className="relative z-10 font-mono text-[10px] text-cream/40">
                    © {new Date().getFullYear()} CAFE HIDEOUT
                </div>
            </div>

            {/* Right form */}
            <div className="flex-1 flex items-center justify-center p-8 md:p-12">
                <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
                    <div className="tracked text-coffee/50 text-[10px] mb-4">Sign In</div>
                    <h2 className="font-editorial font-bold text-coffee text-4xl md:text-5xl leading-tight">
                        Hello, owner.
                    </h2>
                    <p className="font-body text-coffee/60 mt-3 mb-10">
                        Sign in to manage Cafe Hideout.
                    </p>

                    <div className="space-y-7">
                        <div>
                            <label className="tracked text-coffee/60 text-[10px] block mb-3">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email"
                                className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="tracked text-coffee/60 text-[10px] block mb-3">Password</label>
                            <div className="relative">
                                <input
                                    type={showPwd ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="login-password"
                                    className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 pr-10 text-coffee font-body outline-none"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((s) => !s)}
                                    className="absolute right-0 top-3 text-coffee/60"
                                    aria-label="toggle password visibility"
                                >
                                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-pill btn-pill-coffee mt-10 w-full justify-center disabled:opacity-50"
                        data-testid="login-submit"
                    >
                        {loading ? "Signing in…" : "Enter Hideout"} <ArrowRight size={14} />
                    </button>

                    <div className="mt-6 flex items-center justify-between">
                        <Link
                            to="/admin/forgot-password"
                            data-testid="forgot-password-link"
                            className="tracked text-[10px] text-coffee/60 hover:text-gold transition-colors"
                        >
                            Forgot password?
                        </Link>
                        <div className="font-mono text-[10px] text-coffee/40">
                            Owner access only.
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
